import {
  ArrowDownCircleIcon,
  ArrowUpOnSquareIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';

import { Modal } from '@/components/Modal';
import { Table } from '@/components/table/Table';
import { Columns } from '@/components/table/types';
import { Button } from '@/components/ui/button';
import { useGetCollaborators } from '@/hooks/collaborators';
import { useExportRisks } from '@/hooks/useExportRisks';
import { useAuth } from '@/state/auth';

interface TableData {
  displayName: string;
  email: string;
  total: number;
  i: number;
  l: number;
  m: number;
  h: number;
  c: number;
}

export const CollaboratingWith = () => {
  const [collaborator, setCollaborator] = React.useState<TableData>();
  const [showModal, setShowModal] = React.useState(false);
  const { startImpersonation } = useAuth();
  const { mutateAsync: exportRisksFn, status: expostRiskStatus } =
    useExportRisks();

  const isDownloading = expostRiskStatus === 'pending';

  const { data: collaborators, status } = useGetCollaborators({
    getRiskCounts: true,
  });

  const collaboratorsWithCount = useMemo(() => {
    return collaborators.map((collaborator): TableData => {
      const counts = {
        total: 0,
        i: 0,
        l: 0,
        m: 0,
        h: 0,
        c: 0,
      };

      if (collaborator?.counts && collaborator.counts.statistics) {
        // The statistics endpoint returns an array of stats
        collaborator.counts.statistics
          .filter(({ created }) => created === 'now')
          .forEach(stat => {
            // Extract severity from the status string (e.g., "status:O[severity]")
            const severity = stat.value?.toLowerCase();
            if (severity && stat.data.count) {
              counts[severity as keyof typeof counts] = stat.data.count;
            }
          });

        // Calculate total from the sum of all severities
        counts.total = counts.i + counts.l + counts.m + counts.h + counts.c;
      }

      return {
        displayName: collaborator.displayName,
        email: collaborator.email,
        ...counts,
      };
    });
    // eslint-disable-next-line react-hooks/use-memo
  }, [JSON.stringify(collaborators)]);

  const exportRisks = async (email: string) => {
    exportRisksFn({ email });
    setShowModal(false);
  };

  const columns: Columns<TableData> = [
    {
      label: 'User',
      id: 'displayName',
      onClick: (row: TableData) => startImpersonation(row.email),
      cell: row => (
        <span className="text-chariot-text-primary w-full text-wrap break-all">
          {row.displayName || row.email || '-'}
        </span>
      ),
    },
    {
      label: 'Total',
      id: 'total',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'Info',
      id: 'i',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'Low',
      id: 'l',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'Medium',
      id: 'm',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'High',
      id: 'h',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'Critical',
      id: 'c',
      fixedWidth: 80,
      align: 'center',
    },
    {
      label: 'Export',
      id: 'total',
      fixedWidth: 80,
      align: 'center',
      cell: (row: TableData) => (
        <button
          onClick={() => {
            setCollaborator(row);
            setShowModal(true);
          }}
          data-testid={`export-risks-${row.email}`}
          aria-label={`Export risks for ${row.displayName || row.email}`}
        >
          <ArrowUpOnSquareIcon className="size-5" />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="flex w-full flex-col">
        <Table
          name="collaborators"
          columns={columns}
          data={collaboratorsWithCount}
          status={status}
          loadingRowCount={3}
          noData={{
            description: 'No collaborating organizations found.',
          }}
          error={null}
          selection={{
            hide: true,
          }}
        />
      </div>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={
          <div>{`${collaborator?.displayName || collaborator?.email}'s Export`}</div>
        }
        icon={<ArrowDownCircleIcon className="size-5" />}
        size="lg"
        hideFooter
      >
        <div className="flex flex-col space-y-4">
          <p className="text-chariot-text-primary text-xl font-semibold">
            Simplify your export process
          </p>
          <p className="text-md text-chariot-text-secondary mb-3">
            For a smoother experience, try our command line tool. You can
            install it with
            <code className="bg-chariot-background-secondary ml-1 inline-block rounded p-1 py-0.5">
              pip install praetorian-cli
            </code>
            . For more details, check the
            <a
              href="https://github.com/praetorian-inc/praetorian-cli"
              target="_blank"
              rel="noopener noreferrer"
              className="text-chariot-brand-primary ml-1 hover:underline"
            >
              README
            </a>
            .
          </p>
          <div />
          <p className="text-chariot-text-secondary text-sm">
            Click below to download all open risks for this organization.
          </p>
          <div className="flex flex-row space-x-2">
            <Button
              onClick={() => collaborator && exportRisks(collaborator.email)}
              className="w-full"
              disabled={isDownloading ?? false}
            >
              Export Risks
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
